import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Typography,
  App,
  Button,
} from 'antd';
import { LeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { getCompetitionRankingVideos } from '../../services/competitions';
import { Tooltip } from 'antd';

const { Title } = Typography;
const { useApp } = App;

const CompetitionEvaluationsPage = () => {
  const { message } = useApp();
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = useCallback(
    async (page = 1, pageSize = 10) => {
      setLoading(true);
      try {
        const { items, total } =
          await getCompetitionRankingVideos(
            id,
            null, // sin límite
            page,
            pageSize
          );

        const sorted = [...(items || [])].sort(
          (a, b) =>
            a.position - b.position ||
            b.globalScore - a.globalScore
        );

        // Detectar jueces dinámicamente
        const judgesMap = new Map();
        sorted.forEach(video => {
          video.evaluations?.forEach(ev => {
            judgesMap.set(ev.judge.id, ev.judge);
          });
        });

        setJudges(Array.from(judgesMap.values()));
        setData(sorted);

        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar las evaluaciones');
      } finally {
        setLoading(false);
      }
    },
    [id, message]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line
  }, [id]);

  const applyFilters = () => {
    
    fetchData(
      1,
      pagination.pageSize,
    );
  };

  const handleSearch = () => {
    applyFilters();
  };

  const handleTableChange = (pag) => {
    fetchData(pag.current, pag.pageSize);
  };

  const judgeColumns = judges.map(judge => ({
    title: `Juez ${judge.firstName} ${judge.lastName}`,
    align: 'center',
    render: (_, row) => {
      const evaluation = row.evaluations?.find(
        ev => ev.judge.id === judge.id
      );
      return evaluation
        ? Number(evaluation.judgeScore).toFixed(3)
        : '-';
    }
  }));

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            type="text" 
            icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
            style={{ 
              padding: 0,
              color: '#BA955E',
              fontSize: '24px',
              fontWeight: '700',
              height: 'auto'
            }}
            onClick={() => navigate(-1)}
          />
          <Title level={3} style={{ marginBottom: 0 }}>Detalles del historial de evaluación</Title>
        </div>
        <Tooltip title="Recargar">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleSearch}
            />
          </Tooltip>
      </div>

      <Card>

        <Table
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          dataSource={data}
          columns={[
            {
              title: 'Participante',
              render: (_, r) =>
                (`${r.participant?.firstName ?? ''} ${r.participant?.lastName ?? ''} ${r.participant?.secondLastName ?? ''}`.trim()) || r.playerName
            },
            ...judgeColumns,
            {
              title: 'Promedio jueces',
              dataIndex: 'totalScore',
              align: 'center',
              render: v =>
                Number(v || 0).toFixed(3)
            },
            {
              title: 'IA',
              dataIndex: 'aiTotalScore',
              align: 'center',
              render: v =>
                (Number(v || 0) * 10).toFixed(3)
            },
            {
              title: 'Total',
              dataIndex: 'juryScore',
              align: 'center',
              render: v =>
                Number(v || 0).toFixed(3)
            },
            {
              title: 'Puntaje votaciones',
              dataIndex: 'publicScore',
              align: 'center',
              render: v =>
                (Number(v || 0)).toFixed(3)
            },
            {
              title: 'Evaluación Final',
              dataIndex: 'globalScore',
              align: 'center',
              render: v =>
                Number(v || 0).toFixed(3)
            },
          ]}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: t =>
              `Total: ${t} evaluaciones`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default CompetitionEvaluationsPage;